import BootcampTabs from "@/components/Bootcamp/BootcampTabs";
import FAQs from "@/components/Bootcamp/FAQs";
import { Button } from "@/components/ui/button";
import { BootcampSlugData } from "@/types/bootcamp-slug";
import { getBootcampBySlug } from "@/utils/bootcamp";
import { Calendar, Clock, Star } from "lucide-react";
import { GetServerSidePropsContext } from "next";
import Image from "next/image";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import TradewiseAppPromo from "@/components/TradewiseAppPromo";
import BootcampForm from "@/components/Bootcamp/Bootcampform";
import Footer from "@/components/layout/footer";
import useGetAmount from "@/hooks/getAmount";
import { useRouter } from "next/router";
import axios from "axios";
import { useEffect } from "react";
import mixpanel from "@/utils/mixpanel";
import Head from "next/head";
import Breadcrumb from "@/components/BreadCrumb";

dayjs.extend(advancedFormat);

export async function getServerSideProps(
  context: GetServerSidePropsContext<{ id: string }>,
) {
  const slug = context.params?.id;

  if (!slug)
    return {
      notFound: true,
    };

  let bootcampCoupons;
  try {
    const { data } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}coupons/bootcamp/${slug}`,
      {
        headers: {
          "Accept-Encoding": "gzip",
        },
      },
    );

    bootcampCoupons = data.data;
  } catch (error) {
    console.log("ERROR while fetching bootcamp coupons", error);
    bootcampCoupons = [];
  }

  const bootcamp = await getBootcampBySlug(slug);
  if (!bootcamp)
    return {
      notFound: true,
    };
  return {
    props: {
      bootcamp,
      coupons: bootcampCoupons,
    },
  };
}

export default function SingleBootcampPage({
  bootcamp,
  coupons,
}: {
  bootcamp: BootcampSlugData["data"];
  coupons: any;
}) {
  const router = useRouter();

  const amount = useGetAmount({
    baseAmount: bootcamp.Bootcamp.discountedPrice.toString(),
    baseBundleAmount:
      bootcamp.Bootcamp?.bundleBootcamp?.discountedPrice?.toString() ?? "",
    token: router.query?.token,
    originalAmount: bootcamp.Bootcamp.price.toString(),
  });
  const {
    isToken,
    mainButtonAmount,
    BaseAmount,
    isDiscounted,
    discountedToken,
  } = amount;

  useEffect(() => {
    if (coupons) {
      localStorage.setItem("coupons", JSON.stringify(coupons));
    }
  }, [coupons]);

  return (
    <>
      <Head>
        <title>
          Paid BootCamp - {bootcamp?.Bootcamp?.teacher?.name} | Tradewise
        </title>
        <meta name="description" content={bootcamp?.Bootcamp?.description} />
      </Head>
      <main className="min-h-screen h-full space-y-4">
        <section className="p-4 md:p-8 lg:p-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-10">
          <div className="lg:col-span-2 space-y-4">
            <Breadcrumb classNames="max-md:text-xs" />
            <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold">
              {bootcamp?.Bootcamp?.title}
            </h1>
            <section className="grid lg:grid-cols-2 gap-4">
              {bootcamp?.Bootcamp?.coverImage && (
                <Image
                  className="rounded-lg shadow-xl"
                  src={bootcamp.Bootcamp.coverImage}
                  alt={bootcamp.Bootcamp.title}
                  width={1000}
                  height={500}
                />
              )}
              <div className="p-4 flex flex-col gap-4 justify-between">
                <div className="flex md:flex-col gap-4 justify-between">
                  <span className="flex items-center gap-1">
                    <Calendar className="mr-1" />
                    <span className="max-md:hidden">Starts on</span>
                    <span className="font-semibold md:text-orange-500 max-md:hidden">
                      {dayjs(bootcamp?.startDateTime).format("Do MMMM (dddd)")}
                    </span>
                    <span className="md:hidden">
                      {dayjs(bootcamp?.startDateTime).format("Do MMMM (ddd)")}
                    </span>
                  </span>

                  <span className="flex items-center gap-1">
                    <Clock className="mr-1" />
                    Duration:
                    <span className="md:font-semibold md:text-orange-500">
                      {bootcamp?.Bootcamp?.duration}{" "}
                      {bootcamp?.Bootcamp?.durationType}
                    </span>
                  </span>
                </div>
                <hr />
                <div className="flex flex-row-reverse max-md:justify-between md:flex-col gap-3">
                  <div className="flex items-center gap-0.5">
                    <span className="max-md:hidden">Rating:</span>
                    <span className="flex items-center">
                      {Array(5)
                        .fill(0)
                        .map((_, i) => (
                          <Star
                            key={i}
                            className="text-yellow-500 fill-yellow-500"
                            size={16}
                          />
                        ))}
                    </span>
                    <span className="text-sm">
                      {bootcamp?.Bootcamp?.slug ===
                      "secret-60-mentorship-program-with-tradewise"
                        ? "40+ students"
                        : "10k+ students"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="max-md:hidden text-xl text-gray-500 font-medium">
                      {isToken
                        ? "Token"
                        : isDiscounted
                          ? "Discounted"
                          : "Exclusive"}{" "}
                      Price
                    </span>

                    <div className="flex items-center gap-3">
                      <strong className="text-xl max-md:font-semibold md:text-3xl lg:text-4xl text-orange-500">
                        {Number(
                          isToken
                            ? mainButtonAmount
                            : isDiscounted
                              ? mainButtonAmount
                              : bootcamp?.Bootcamp?.discountedPrice,
                        ).toLocaleString("en-US", {
                          style: "currency",
                          currency: "INR",
                          minimumFractionDigits: 0,
                        })}
                      </strong>
                      <span className="text-sm md:text-xl lg:text-2xl text-gray-500 line-through">
                        {Number(bootcamp.Bootcamp.price).toLocaleString(
                          "en-US",
                          {
                            style: "currency",
                            currency: "INR",
                            minimumFractionDigits: 0,
                          },
                        )}
                      </span>
                    </div>
                    {isToken && (
                      <div className="text-foreground text-sm font-medium">
                        This is a token payment. Please pay{" "}
                        <span className="font-bold">₹{mainButtonAmount}</span>{" "}
                        now and the remaining amount of{" "}
                        <span className="font-bold">
                          ₹
                          {parseInt(BaseAmount, 10) -
                            parseInt(mainButtonAmount, 10)}
                        </span>{" "}
                        will be due later.
                      </div>
                    )}
                  </div>
                </div>

                <a href="#form" className="md:hidden w-full">
                  <Button
                    className="rounded-lg text-xl py-6 uppercase w-full"
                    onClick={() => {
                      mixpanel.track("enroll_now", {
                        bootcamp_title: bootcamp?.Bootcamp?.title,
                        cta_position: "top",
                        source: "astrolearn",
                      });
                    }}
                  >
                    Enroll Now
                  </Button>
                </a>
              </div>
            </section>

            <BootcampTabs bootcamp={bootcamp} />
            <div className="md:hidden">
              <BootcampForm
                amount={amount}
                bootcamp={bootcamp}
                coupons={coupons}
              />
            </div>
            <FAQs />
            <TradewiseAppPromo />
          </div>
          <div className="max-md:hidden">
            <BootcampForm
              amount={amount}
              bootcamp={bootcamp}
              coupons={coupons}
            />
          </div>
        </section>
        <Footer hidden={false} phone="+918048822399" />
      </main>
    </>
  );
}
